import { compare, compareNumber } from '../../core/helpers/sorting'

import { FacetGroup } from '../models/facet-group'
import { PreppedGroups } from '../models/prepped-groups'
import { Facet } from '../models/facet'
import { FacetCount } from '../models/facet-count'
import { UXFacet, UXFacetChip, UXFacetGroup } from '../models/ux-facets'
import { AppliedFacets } from '../models/applied-facets'

const empty: any = {}
const emptyPredicate = result => result !== empty

export function prepFacetGroups(groups: FacetGroup[]): PreppedGroups {
  return groups.reduce(
    (dict, group) => Object.assign(dict, { [group.code]: group }),
    {}
  )
}

export function prepFacets(facets: Facet[]): { [group: string]: Facet[] } {
  return facets.reduce(
    (dict, facet) =>  {
      let groupCode = facet.code.split('=')[0]

      return Object.assign(
        dict,
        {
          [groupCode]: [
            ...dict[groupCode] || [],
            facet
          ]
        })
    },
    {}
  )
}

export function parseFromBaseline(groups, baseline, incomingCounts): { [group: string]: FacetCount[] } {
  return groups.reduce(
    (prev, curr) => {
      let group = baseline[curr]

      return Object.assign(
        prev,
        {
          [curr]: group.map(count => {
            let incoming = incomingCounts
              .find(fc => fc._id === count._id)

            return {
              ...count,
              count: (incoming && incoming.count) || 0
            }
          })
        })
    },
    {} as { [group: string]: FacetCount[] }
  )
}

export function applyOver(incoming): { [group: string]: FacetCount[] } {
  let dict = {}

  if (!incoming)
    return dict

  incoming.forEach((count: FacetCount) => {
    let groupCode = count._id.split('=')[0]
    let entry = dict[groupCode]

    if(!entry)
      dict[groupCode] = entry = []

    if(entry.indexOf(entry.find(obj => obj._id === count._id)) < 0)
      entry.push(count)
  })

  return dict
}

export function createUXFacets(facets: FacetCount[], baseline: Facet[], groupCode?: string): UXFacet[] {
  if(!baseline)
    return []

  return facets.map(facetCount => {
    let facet = baseline.find(facet => facet.code === facetCount._id)

    if(!facet) return empty

    return {
      name: facet.name,
      code: facet.code.split('=')[1],
      count: facetCount.count,
      group: groupCode
    }
  })
    .filter(emptyPredicate)
    .sort(compare<UXFacet>(facet => facet.name))
}

export function createUXFacetGroups(
  groupNames: string[],
  groups: PreppedGroups,
  facets: { [group: string]: Facet[] },
  facetCounts: { [group: string]: FacetCount[] }
): UXFacetGroup[] {
  return groupNames.map(curr => {
    let facetsGrouping = facetCounts[curr]
    let baselineGrouping = facets[curr]
    let facetGroup = groups[curr]

    if(!facetsGrouping || !baselineGrouping || !facetGroup)
      return empty
    return {
      name: facetGroup.name,
      code: curr,
      facets: createUXFacets(facetsGrouping, baselineGrouping, facetGroup.code),
      sortOrder: facetGroup.sortOrder
    }
  })
    .filter(emptyPredicate)
    .sort(compareNumber<UXFacetGroup>(group => group.sortOrder))
}

export function createUXFacetChips(
  groupnames: string[],
  uxfacets: UXFacetGroup[],
  appliedFacets: AppliedFacets
): UXFacetChip[] {
  if (groupnames && groupnames.length > 0 && (uxfacets && uxfacets.length > 0) && (appliedFacets)) {
    return groupnames.reduce(
      (prev, curr) => {
        let applied = appliedFacets[curr]

        if(!applied) return prev

        let group = uxfacets.find(group => group.code === curr)

        applied.forEach(facetCode => {
          let facet = group.facets.find(f => f.code === facetCode)

          if(!facet)
            return

          prev.push({
            code: facet.code,
            name: facet.name,
            group: group.code,
            groupName: group.name
          } as UXFacetChip)
        })
        return prev
      },
      [] as UXFacetChip[]
    ).sort(compare<UXFacetChip>(chip => chip.groupName))
  }
  return [] as UXFacetChip[]
}
